
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import { useStripePayment } from "@/hooks/useStripePayment"
import { usePaymentReceipt } from "@/hooks/usePaymentReceipt"
import { StripePaymentElement } from "./StripePaymentElement"
import { EmailDebugInfo } from "./EmailDebugInfo"

interface PaymentFormProps {
  clientSecret: string | null
  processing: boolean
  onCancel: () => void
  onSuccess?: () => void
  formData: any
  selectedDeliveryOption?: string
  amount?: number
}

export function PaymentForm({ 
  clientSecret, 
  processing, 
  onCancel, 
  onSuccess,
  formData,
  selectedDeliveryOption,
  amount 
}: PaymentFormProps) {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { stripe, elements, initError } = useStripePayment(clientSecret)
  
  const { 
    emailSending, 
    emailResponse, 
    emailDebugInfo, 
    sendPaymentReceiptEmail 
  } = usePaymentReceipt({
    formData,
    selectedDeliveryOption,
    amount
  })

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setIsSubmitting(true);
    
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        console.error('Payment confirmation error:', error);
        toast({
          variant: "destructive",
          title: "Payment Error",
          description: error.message || "Payment failed. Please try again.",
        });
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('Payment successful, sending receipt email');
        
        // Send receipt email after successful payment
        const emailResult = await sendPaymentReceiptEmail();
        
        console.log('Email sending result:', emailResult, 'Email response:', emailResponse);
        
        toast({
          title: "Payment Successful",
          description: "Thank you for your purchase! We'll process your order right away.",
        });
        
        // Continue even if email sending fails
        onSuccess?.();
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const isProcessing = processing || emailSending || isSubmitting;
  const isPaymentDisabled = !stripe || !elements || !clientSecret || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <StripePaymentElement initError={initError} onCancel={onCancel} />
      
      <EmailDebugInfo error={emailResponse?.error} emailDebugInfo={emailDebugInfo} />
      
      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Back
        </Button>
        <Button
          type="submit"
          disabled={isPaymentDisabled}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {isProcessing ? "Processing..." : "Pay Now"}
        </Button>
      </div>
    </form>
  )
}
